import { useEffect, useState } from 'react'
import {
  useMissingReadingsConfig,
  useUpdateMissingReadingsConfig,
} from '../../../hooks/admin/useMissingReadings'
import type { MissingReadingsConfig } from '../../../types/missingReadings'
import { Card, Field, NumberField, Toggle } from './fields'

type FormState = Omit<MissingReadingsConfig, 'id'>

/**
 * Admin-only: how strict the "Missing" column on the report view is.
 *
 * The column itself always shows the exact Sigfox sequence-gap count; this
 * threshold only decides when that count is flagged, against the device's
 * expected reading count for the period.
 */
export function MissingReadingsSettingsSection() {
  const { data: config, isLoading } = useMissingReadingsConfig()
  const updateConfig = useUpdateMissingReadingsConfig()
  const [form, setForm] = useState<FormState | null>(null)

  useEffect(() => {
    if (config) {
      const { id, ...rest } = config
      setForm(rest)
    }
  }, [config])

  if (isLoading || !form) {
    return (
      <Card title="Missing readings">
        <p className="text-gray-600">Loading...</p>
      </Card>
    )
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  return (
    <Card title="Missing readings">
      <p className="text-sm text-gray-600 mb-4">
        Flags a flow sensor's row on the report view when its exact missing-message count (from gaps
        in the device's Sigfox sequence number) exceeds this percent of the readings expected for the
        period, given the device's configured interval. Admin-only — nobody outside sees this.
      </p>
      <Toggle
        label="Enabled"
        checked={form.enabled}
        onChange={(v) => set('enabled', v)}
        hint="While off, the Missing column still shows counts but nothing is flagged."
      />
      <Field label="Tolerance (%)" hint="Default 5%. E.g. 5% of 96 expected daily readings allows 4 missing before flagging.">
        <NumberField
          value={form.tolerancePercent}
          onChange={(v) => set('tolerancePercent', v ?? 5)}
          min={0}
          max={100}
        />
      </Field>
      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={() => updateConfig.mutate(form)}
          disabled={updateConfig.isPending}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {updateConfig.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </Card>
  )
}
